/**
 * Servicio de Firebase Authentication usando AngularFire
 * Maneja todas las operaciones de autenticación con Firebase
 */
import { Injectable, inject } from '@angular/core';
import { 
  Auth,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  sendPasswordResetEmail,
  sendEmailVerification,
  updateProfile,
  GoogleAuthProvider,
  signInWithPopup,
  UserCredential,
  User,
  fetchSignInMethodsForEmail
} from '@angular/fire/auth';
import { getAdditionalUserInfo, AdditionalUserInfo, AuthCredential, signInWithCredential as firebaseSignInWithCredential, GoogleAuthProvider as FirebaseGoogleAuthProvider } from 'firebase/auth';
import { authState } from '@angular/fire/auth';
import { Observable, from, of, BehaviorSubject, throwError } from 'rxjs';
import { map, catchError, switchMap, tap } from 'rxjs/operators';
import { IUser, IAuthTokens } from '../models/auth.types';
import { FirebaseService } from './firebase.service';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root'
})
export class FirebaseAuthService {
  private firebaseService = inject(FirebaseService);
  private auth: Auth;
  
  // Observable para el estado de autenticación usando AngularFire
  public authState$: Observable<User | null>;

  constructor() {
    this.auth = this.firebaseService.getAuth();
    
    // Usar authState de AngularFire para observar cambios en el estado de autenticación
    this.authState$ = authState(this.auth);
  }

  /**
   * Inicia sesión con email y contraseña
   * @param email Email del usuario
   * @param password Contraseña del usuario
   * @returns Observable con las credenciales del usuario
   */
  loginWithEmail(email: string, password: string): Observable<UserCredential> {
    return from(signInWithEmailAndPassword(this.auth, email, password));
  }

  /**
   * Registra un nuevo usuario con email y contraseña
   * @param email Email del usuario
   * @param password Contraseña del usuario
   * @param displayName Nombre completo del usuario (opcional)
   * @returns Observable con las credenciales del usuario
   */
  registerWithEmail(
    email: string, 
    password: string, 
    displayName?: string
  ): Observable<UserCredential> {
    return from(createUserWithEmailAndPassword(this.auth, email, password)).pipe(
      switchMap((userCredential) => {
        // Crear una promesa para actualizar el perfil y enviar el email de verificación
        const updatePromises: Promise<void>[] = [];
        
        // Si se proporciona un nombre, actualizar el perfil
        if (displayName && userCredential.user) {
          updatePromises.push(updateProfile(userCredential.user, { displayName }));
        }
        
        // Enviar email de verificación
        if (userCredential.user) {
          updatePromises.push(sendEmailVerification(userCredential.user));
        }
        
        // Esperar a que todas las operaciones terminen
        return from(Promise.all(updatePromises)).pipe(
          map(() => userCredential)
        );
      })
    );
  }

  /**
   * Inicia sesión con Google
   * @returns Observable con las credenciales del usuario
   */
  loginWithGoogle(): Observable<UserCredential> {
    const provider = new GoogleAuthProvider();
    // Configurar permisos adicionales si es necesario
    provider.addScope('profile');
    provider.addScope('email');
    
    return from(signInWithPopup(this.auth, provider));
  }

  /**
   * Cierra la sesión del usuario actual
   * @returns Observable que se completa cuando se cierra la sesión
   */
  logout(): Observable<void> {
    return from(signOut(this.auth));
  }

  /**
   * Obtiene los métodos de autenticación disponibles para un email
   * @param email Email del usuario
   * @returns Observable con array de métodos (ej: ['password', 'google.com'])
   */
  getSignInMethodsForEmail(email: string): Observable<string[]> {
    return from(fetchSignInMethodsForEmail(this.auth, email));
  }

  /**
   * Envía un email para restablecer la contraseña
   * @param email Email del usuario
   * @returns Observable que se completa cuando se envía el email
   */
  sendPasswordResetEmail(email: string): Observable<void> {
    const actionCodeSettings = {
      url: environment.firebase.auth.actionUrl,
      handleCodeInApp: true
    };
    
    // Intentar enviar directamente - Firebase maneja internamente si el email existe o no
    return from(sendPasswordResetEmail(this.auth, email, actionCodeSettings)).pipe(
      catchError(error => {
        // Si el usuario solo tiene Google, Firebase devuelve este error
        if (error?.code === 'auth/user-not-found') {
          // Por seguridad, no revelamos si el email existe
          return of(undefined as void);
        }
        return throwError(() => error);
      })
    );
  }

  /**
   * Envía un email de verificación al usuario actual
   * @returns Observable que se completa cuando se envía el email
   */
  private lastEmailSentTimestamp = 0;
  private readonly MIN_EMAIL_INTERVAL = 60000; // 1 minuto en milisegundos

  sendVerificationEmail(): Observable<void> {
    const user = this.auth.currentUser;
    if (!user) {
      return throwError(() => new Error('No hay usuario autenticado'));
    }

    // Verificar si ha pasado suficiente tiempo desde el último envío
    const now = Date.now();
    const timeSinceLastEmail = now - this.lastEmailSentTimestamp;
    
    if (timeSinceLastEmail < this.MIN_EMAIL_INTERVAL) {
      const waitTime = Math.ceil((this.MIN_EMAIL_INTERVAL - timeSinceLastEmail) / 1000);
      return throwError(() => new Error(
        `Por favor, espera ${waitTime} segundos antes de solicitar otro correo de verificación.`
      ));
    }

    const actionCodeSettings = {
      url: environment.firebase.auth.actionUrl,
      handleCodeInApp: true
    };

    return from(sendEmailVerification(user, actionCodeSettings)).pipe(
      tap(() => {
        this.lastEmailSentTimestamp = now;
      }),
      catchError(error => {
        if (error?.code === 'auth/too-many-requests' || 
            error?.message?.includes('TOO_MANY_ATTEMPTS_TRY_LATER')) {
          return throwError(() => new Error(
            'Has realizado demasiados intentos. Por favor, espera unos minutos antes de intentar nuevamente.'
          ));
        }
        return throwError(() => error);
      })
    );
  }

  /**
   * Obtiene el usuario actual autenticado
   * @returns Usuario actual o null si no hay usuario autenticado
   */
  getCurrentUser(): User | null {
    return this.auth.currentUser;
  }

  /**
   * Verifica si hay un usuario autenticado
   * @returns true si hay un usuario autenticado, false en caso contrario
   */
  isAuthenticated(): boolean {
    return this.auth.currentUser !== null;
  }

  /**
   * Obtiene el token de ID del usuario actual
   * @returns Observable con el token de ID
   */
  getIdToken(): Observable<string> {
    const user = this.auth.currentUser;
    if (!user) {
      return new Observable<string>(observer => {
        observer.error(new Error('No hay usuario autenticado'));
      });
    }
    return from(user.getIdToken());
  }

  /**
   * Obtiene el token de ID del usuario actual con refresh
   * @param forceRefresh Si es true, fuerza la renovación del token
   * @returns Observable con el token de ID
   */
  getIdTokenResult(forceRefresh: boolean = false): Observable<string> {
    const user = this.auth.currentUser;
    if (!user) {
      return new Observable<string>(observer => {
        observer.error(new Error('No hay usuario autenticado'));
      });
    }
    return from(user.getIdToken(forceRefresh));
  }

  /**
   * Valida y normaliza una URL de avatar
   * Retorna el avatar por defecto si la URL es inválida, vacía o null
   */
  private validateAvatarUrl(url: string | null | undefined): string {
    const defaultAvatar = 'assets/images/default-avatar.png';
    
    if (!url || typeof url !== 'string') {
      return defaultAvatar;
    }
    
    const trimmedUrl = url.trim();
    
    // Si está vacía, es 'null' o 'undefined' como string, usar el avatar por defecto
    if (trimmedUrl === '' || trimmedUrl === 'null' || trimmedUrl === 'undefined') {
      return defaultAvatar;
    }
    
    // Validar que sea una URL válida (debe empezar con http, https, o assets)
    if (trimmedUrl.startsWith('http://') || trimmedUrl.startsWith('https://') || trimmedUrl.startsWith('assets/')) {
      return trimmedUrl;
    }
    
    // Si no es una URL válida, usar el avatar por defecto
    return defaultAvatar;
  }

  /**
   * Convierte un User de Firebase a IUser de la aplicación
   * @param firebaseUser Usuario de Firebase
   * @returns Usuario de la aplicación
   */
  mapFirebaseUserToAppUser(firebaseUser: User): IUser {
    const displayName = firebaseUser.displayName || '';
    const nameParts = displayName.split(' ');
    const firstName = nameParts[0] || '';
    const lastName = nameParts.slice(1).join(' ') || '';

    return {
      id: firebaseUser.uid,
      username: firebaseUser.email?.split('@')[0] || firebaseUser.uid,
      email: firebaseUser.email || '',
      firstName: firstName,
      lastName: lastName,
      roles: [], // Los roles se pueden obtener de Firestore si es necesario
      permissions: [],
      avatar: this.validateAvatarUrl(firebaseUser.photoURL),
      userType: undefined // Se puede determinar según la lógica de negocio
    };
  }

  /**
   * Crea tokens de autenticación a partir del usuario de Firebase
   * @param firebaseUser Usuario de Firebase
   * @returns Observable con los tokens de autenticación
   */
  createAuthTokens(firebaseUser: User): Observable<IAuthTokens> {
    // Obtener el token de ID del usuario específico
    return from(firebaseUser.getIdToken()).pipe(
      map((token: string) => {
        // Firebase maneja los refresh tokens internamente
        // Usamos el token de ID como accessToken y almacenamos el uid como referencia
        return {
          accessToken: token,
          refreshToken: firebaseUser.uid, // Usamos el UID como referencia para refresh
          expiresIn: 3600, // Firebase maneja la expiración automáticamente (1 hora por defecto)
          tokenType: 'Bearer'
        };
      })
    );
  }

  /**
   * Obtiene la información adicional del usuario autenticado
   * @param userCredential Credenciales retornadas por Firebase
   * @returns Información adicional del usuario o null
   */
  getAdditionalUserInfo(userCredential: UserCredential): AdditionalUserInfo | null {
    return getAdditionalUserInfo(userCredential) ?? null;
  }

  /**
   * Obtiene las credenciales OAuth de un resultado de Google
   * @param userCredential Credenciales retornadas por Firebase
   * @returns Credencial de autenticación o null si no está disponible
   */
  getCredentialFromUserCredential(userCredential: UserCredential): AuthCredential | null {
    return FirebaseGoogleAuthProvider.credentialFromResult(userCredential) ?? null;
  }

  /**
   * Inicia sesión usando directamente una credencial OAuth
   * @param credential Credencial de autenticación
   * @returns Observable con las credenciales del usuario
   */
  signInWithCredential(credential: AuthCredential): Observable<UserCredential> {
    return from(firebaseSignInWithCredential(this.auth, credential));
  }

  /**
   * Actualiza el perfil del usuario autenticado en Firebase
   * @param payload Datos a actualizar
   * @returns Observable que se completa al finalizar la actualización
   */
  updateCurrentUserProfile(payload: { displayName?: string; photoURL?: string }): Observable<void> {
    const user = this.auth.currentUser;

    if (!user) {
      return throwError(() => new Error('No hay usuario autenticado'));
    }

    return from(updateProfile(user, {
      displayName: payload.displayName ?? user.displayName ?? '',
      photoURL: payload.photoURL ?? user.photoURL ?? undefined
    })).pipe(map(() => void 0));
  }
}

